import { useUiStore } from '@/store/ui-store';
import { THEME_LIST, type ThemeName } from '@/themes/theme-config';
import { useTranslation } from 'react-i18next';

/**
 * Theme picker — renders the 4 themes as a vertical button list inside
 * the sidebar's "Görünüm" sub-panel (Phase 11 batch 2.7). Pure CSS
 * variable switching, no React re-render of the dashboard.
 */
export function ThemeSwitcher(): JSX.Element {
  const { t } = useTranslation();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  return (
    <div className="flex flex-col gap-1" id="theme-switcher" role="radiogroup">
      {THEME_LIST.map((opt) => {
        const selected = opt.id === theme;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => setTheme(opt.id as ThemeName)}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs"
            style={{
              border: `1px solid ${
                selected ? 'var(--color-accent)' : 'var(--color-border)'
              }`,
              background: selected ? 'var(--color-accent-subtle)' : 'transparent',
              color: 'var(--color-fg)',
              fontWeight: selected ? 600 : 400,
            }}
          >
            <span
              className="inline-block rounded-full shrink-0"
              style={{
                width: 10,
                height: 10,
                background: opt.swatch,
              }}
              aria-hidden
            />
            {t(`theme.options.${opt.id}`)}
            {selected && (
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                className="ml-auto"
                aria-hidden
              >
                <path
                  d="M3 8l4 4 6-7"
                  stroke="var(--color-accent)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}
